import { WithApiRequestMeta } from "../../api-results";
import { RtkBaseQuery, RtkEmptySplitApi } from "../../types";
import { fetchAllPages } from "../../utils";
import {
  EntityInvitationModel,
  EntityModel,
  SubscriptionInvoiceModel,
  SubscriptionInfoModel,
  SubscriptionPlanModuleId,
} from "./models";
import { EntityDto } from "./models/Entity/EntityDto";
import { transformEntityDtoToModel } from "./models/Entity/transformers";
import { EntityInvitationDto } from "./models/EntityInvitation/EntityInvitationDto";
import { transformEntityInvitationDtoToModel } from "./models/EntityInvitation/transformers";
import { SubscriptionPlanModel } from "./models/SubscriptionPlan/SubscriptionPlanModel";
import { SubscriptionPlanDto } from "./models/SubscriptionPlan/SubscriptionPlanDto";
import { transformSubscriptionPlansDtoToModels } from "./models/SubscriptionPlan/transformers";
import { RespondToEntityInviteMutationPayload } from "./payloads";
import { EntitiesApiTag } from "./tags";
import {
  EntityUpdateIndicator,
  ModuleUnreadIndicators,
  UnreadIndicatorSupportedModule,
  InvitationLinkResponse,
  UserEntityPreferences,
  EntityTemplates,
} from "./types";
import { SubscriptionCustomerAddress } from "./models/common-types/SubscriptionCustomerAddress";
import { PromoCodeInfo } from "./models/common-types/PromoCodeInfo";
import { transformSubscriptionInfoDtoToModel } from "./models/SubscriptionInfo/transformers";
import { SubscriptionInfoDto } from "./models/SubscriptionInfo/SubscriptionInfoDto";

export const addEntitiesApi = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [
      EntitiesApiTag.getEntity,
      EntitiesApiTag.getEntityList,
      EntitiesApiTag.getSubscriptionInfo,
      EntitiesApiTag.getInvoicesList,
      EntitiesApiTag.getUserEntityPreferences,
      EntitiesApiTag.getSettings,
    ],
  });

  const entitiesApi = apiWithTags.injectEndpoints({
    endpoints: (builder) => ({
      createEntity: builder.mutation<
        EntityModel,
        WithApiRequestMeta<{
          data: Partial<
            Pick<EntityDto, "name" | "country" | "billing_email" | "currency">
          >;
          type?: "org" | "cx";
          template?: {
            step1?: Partial<EntityTemplates["step1_options"]>;
            step2?: Partial<EntityTemplates["step2_options"]>;
          };
          displayImg?: File;
        }>
      >({
        query: ({ data, displayImg, template, type }) => {
          const binData = new FormData();

          binData.append(
            "entity",
            JSON.stringify({
              ...data,
              type: type ?? "org",
            })
          );
          if (template) {
            binData.append(
              "template",
              JSON.stringify({
                ...template,
              })
            );
          }
          if (displayImg) {
            binData.append("display_pic_file", displayImg);
          }

          return {
            url: `/api/auth/entity`,
            method: "POST",
            data: binData,
          };
        },
        onQueryStarted: ({ type }, { dispatch, queryFulfilled }) => {
          if (type === "cx") return;
          queryFulfilled
            .then((result) => {
              dispatch(
                entitiesApi.util.updateQueryData(
                  "_DEPRECATED_getAllOrgEntitiesList",
                  undefined,
                  (entities) => {
                    entities.unshift(result.data);
                  }
                )
              );
            })
            .catch(() => {});
        },
        invalidatesTags: (result, error) =>
          error ? [] : [EntitiesApiTag.getEntityList],
        transformResponse: (response: EntityDto) =>
          transformEntityDtoToModel(response),
      }),

      claimEntity: builder.mutation<
        EntityModel,
        WithApiRequestMeta<{
          entityId: string;
          data: {
            country: string;
            name: string;
            desc?: string;
            billing_email?: string;
          };
        }>
      >({
        query: ({ entityId, data }) => {
          const binData = new FormData();

          binData.append(
            "entity",
            JSON.stringify({
              ...data,
              type: "cx",
            })
          );

          return {
            url: `/api/auth/entity/${entityId}/claim`,
            method: "POST",
            data: binData,
          };
        },
        invalidatesTags: (result, error) =>
          error ? [] : [EntitiesApiTag.getEntityList],
        transformResponse: (response: EntityDto) =>
          transformEntityDtoToModel(response),
      }),

      editEntity: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          data: Partial<
            Pick<EntityDto, "name" | "country" | "billing_email" | "currency">
          >;
          displayImg?: File;
        }>
      >({
        query: ({ data, entityId, displayImg }) => {
          const binData = new FormData();

          binData.append("entity", JSON.stringify(data));
          if (displayImg) {
            binData.append("display_pic_file", displayImg);
          }

          return {
            url: `/api/auth/entity/${entityId}`,
            method: "PUT",
            data: binData,
          };
        },
        onQueryStarted: ({ data, entityId }, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            entitiesApi.util.updateQueryData(
              "_DEPRECATED_getAllOrgEntitiesList",
              undefined,
              (entities) =>
                entities.map((entity) =>
                  entity.id === entityId ? { ...entity, ...data } : entity
                )
            )
          );

          queryFulfilled.catch(patchResult.undo);
        },
        invalidatesTags: (e0, e1, { entityId }) => [
          EntitiesApiTag.getEntityList,
          { type: EntitiesApiTag.getEntity, id: entityId },
        ],
      }),

      deleteEntity: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/entity/${entityId}`,
          method: "DELETE",
        }),
        onQueryStarted: ({ entityId }, { dispatch, queryFulfilled }) => {
          queryFulfilled.then(() => {
            dispatch(
              entitiesApi.util.updateQueryData(
                "_DEPRECATED_getAllOrgEntitiesList",
                undefined,
                (entities) => entities.filter((item) => item.id !== entityId)
              )
            );
          });
        },
      }),

      getEntity: builder.query<
        EntityModel,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/entity/${entityId}`,
        }),
        providesTags: (e0, e1, { entityId }) => [
          { type: EntitiesApiTag.getEntity, id: entityId },
        ],
        transformResponse: (dto: EntityDto) => transformEntityDtoToModel(dto),
      }),

      getUserEntityPreferences: builder.query<
        UserEntityPreferences,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/entity/${entityId}/preferences`,
        }),
        providesTags: [EntitiesApiTag.getUserEntityPreferences],
      }),

      updateUserEntityPreferences: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          data: UserEntityPreferences;
        }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/entity/${entityId}/preferences`,
          method: "POST",
          data: {
            preferences: data,
          },
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            entitiesApi.util.updateQueryData(
              "getUserEntityPreferences",
              { entityId: args.entityId },
              (preferencesDraft) => ({
                media_settings: {
                  ...preferencesDraft.media_settings,
                  ...args.data.media_settings,
                },
                notification_pref: {
                  ...preferencesDraft.notification_pref,
                  ...args.data.notification_pref,
                },
                pinned_modules: {
                  pinnedTools: args.data.pinned_modules?.pinnedTools || [],
                },
              })
            )
          );
          queryFulfilled.catch(patchResult.undo);
        },
        invalidatesTags: [EntitiesApiTag.getUserEntityPreferences],
      }),

      _DEPRECATED_getAllOrgEntitiesList: builder.query<EntityModel[], void>({
        queryFn: async (params, e1, e2, baseQuery) => {
          try {
            const allEntitiesDto = await fetchAllPages<EntityDto>(
              "/api/auth/entity/list",
              { type: "org" },
              baseQuery as unknown as RtkBaseQuery
            );
            return {
              data: allEntitiesDto.map((entity) =>
                transformEntityDtoToModel(entity)
              ),
            };
            // oxlint-disable-next-line typescript/no-explicit-any
          } catch (error: any) {
            return { error };
          }
        },
        providesTags: [EntitiesApiTag.getEntityList],
      }),

      getModuleUnreadIndicators: builder.query<
        ModuleUnreadIndicators,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/entity/${entityId}/visits`,
        }),
        transformResponse: (response: { data: EntityUpdateIndicator[] }) => {
          const state: ModuleUnreadIndicators = {
            task: { my: 0 },
            people: 0,
            drive: 0,
            chat: 0,
            call: 0,
            customer: 0,
          };

          response.data.forEach((indicator) => {
            if (indicator.module === "task") {
              const boardId = indicator.board_id || "my";
              state.task[boardId] = Number(indicator.entity_unread);
            } else {
              state[indicator.module] = Number(indicator.entity_unread);
            }
          });

          return state;
        },
      }),

      markEntityAsUnread: builder.mutation<
        null,
        WithApiRequestMeta<{ entityId: string }>
      >({
        queryFn: () => ({ data: null }),
        onQueryStarted: ({ entityId }, { dispatch, queryFulfilled }) => {
          queryFulfilled.then(() => {
            dispatch(
              entitiesApi.util.updateQueryData(
                "getEntity",
                { entityId },
                (entity) => {
                  entity.unread = true;
                }
              )
            );
            dispatch(
              entitiesApi.util.updateQueryData(
                "_DEPRECATED_getAllOrgEntitiesList",
                undefined,
                (entities) => {
                  const foundEntity = entities.find(
                    (entity) => entity.id === entityId
                  );

                  if (foundEntity) {
                    foundEntity.unread = true;
                  }
                }
              )
            );
          });
        },
      }),

      markEntityAsRead: builder.mutation<
        unknown,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/entity/${entityId}/visits`,
          method: "POST",
          data: {
            type: "user_unread",
          },
        }),
        onQueryStarted: ({ entityId }, { dispatch, queryFulfilled }) => {
          queryFulfilled.then(() => {
            dispatch(
              entitiesApi.util.updateQueryData(
                "getEntity",
                { entityId },
                (entity) => {
                  entity.unread = false;
                }
              )
            );
            dispatch(
              entitiesApi.util.updateQueryData(
                "_DEPRECATED_getAllOrgEntitiesList",
                undefined,
                (entities) => {
                  const foundEntity = entities.find(
                    (entity) => entity.id === entityId
                  );

                  if (foundEntity) {
                    foundEntity.unread = false;
                  }
                }
              )
            );
          });
        },
      }),

      markEntityModuleAsRead: builder.mutation<
        unknown,
        WithApiRequestMeta<{
          entityId: string;
          module: UnreadIndicatorSupportedModule;
          boardId?: string;
        }>
      >({
        query: ({ entityId, module, boardId }) => ({
          url: `/api/auth/entity/${entityId}/visits`,
          // NOTE: No need to show error as user can't do anything about it
          method: "POST",
          data: {
            type: "entity_unread",
            module,
            board_id: boardId,
          },
        }),
      }),

      updateModuleUnreadIndicators: builder.mutation<
        null,
        WithApiRequestMeta<{ entityId: string; state: ModuleUnreadIndicators }>
      >({
        queryFn: () => ({ data: null }),
        onQueryStarted: ({ entityId, state }, { dispatch }) => {
          dispatch(
            entitiesApi.util.updateQueryData(
              "getModuleUnreadIndicators",
              { entityId },
              () => state
            )
          );
        },
      }),

      getAllEntitiesModuleUpdateIndicators: builder.query<
        { data: Record<string, EntityUpdateIndicator[]> },
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/entity/${entityId}/visits`,
          params: { type: "all" },
        }),
        transformResponse: (
          response: {
            data: Record<string, EntityUpdateIndicator[]>;
          },
          _,
          { entityId }
        ) => {
          // Filter out current entity as backend includes it in the response for some reason
          if (entityId in response.data) {
            delete response.data[entityId];
          }

          return response;
        },
      }),

      getLatestInvoice: builder.query<
        SubscriptionInvoiceModel,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/payments/v1/latest_invoice`,
        }),
      }),

      getUpcomingInvoice: builder.query<
        SubscriptionInvoiceModel,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/payments/v1/upcoming_invoice`,
        }),
      }),

      getInvoicesList: builder.query<
        { invoice_list: SubscriptionInvoiceModel[]; remaining_item: boolean },
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/payments/v1/list_invoices`,
        }),
        providesTags: (result, error, { entityId }) => [
          { type: EntitiesApiTag.getInvoicesList, id: entityId },
        ],
      }),

      getSubscriptionInfo: builder.query<
        SubscriptionInfoModel,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/payments/v1/subscription`,
        }),
        transformResponse: (dto: SubscriptionInfoDto) =>
          transformSubscriptionInfoDtoToModel(dto),
        providesTags: (result, error, { entityId }) => [
          { type: EntitiesApiTag.getSubscriptionInfo, id: entityId },
        ],
      }),

      getSubscriptionPlans: builder.query<
        SubscriptionPlanModel[],
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/payments/v1/plans`,
        }),
        transformResponse: (data: { plan_list: SubscriptionPlanDto[] }) =>
          transformSubscriptionPlansDtoToModels(data.plan_list),
      }),

      updateBillingInfo: builder.mutation<
        { result: "success" },
        WithApiRequestMeta<{
          entityId: string;
          data: {
            address: SubscriptionCustomerAddress;
            name?: string;
            notes?: string;
            tax_id?: {
              country: string;
              id: string;
            };
          };
        }>
      >({
        query: ({ entityId, data }) => ({
          method: "PUT",
          url: `/api/auth/${entityId}/payments/v1/billing_info`,
          data,
        }),
        onQueryStarted: ({ entityId, data }, { dispatch, queryFulfilled }) => {
          queryFulfilled.then(() => {
            dispatch(
              entitiesApi.util.updateQueryData(
                "getSubscriptionInfo",
                { entityId },
                (currentData) => {
                  currentData.customerAddress = data.address;
                  currentData.customerName = data.name || "";
                  return currentData;
                }
              )
            );
          });
        },
      }),

      upgradeSubscription: builder.mutation<
        | { result: "success" }
        | { result: "action_required"; secret: string; pub_key: string },
        WithApiRequestMeta<{
          entityId: string;
          data: {
            price_id: string;
            additional_seats?: { id: string; qty: number };
            promo_code?: string | string[];
          };
        }>
      >({
        query: ({ entityId, data }) => ({
          method: "POST",
          url: `/api/auth/${entityId}/payments/v1/change_plan`,
          data,
        }),
        invalidatesTags: (result, error, { entityId }) =>
          result
            ? [{ type: EntitiesApiTag.getInvoicesList, id: entityId }]
            : [],
      }),

      updateSeats: builder.mutation<
        | { result: "success" }
        | { result: "action_required"; secret: string; pub_key: string },
        WithApiRequestMeta<{
          entityId: string;
          data: { qty: number; price_id: string };
        }>
      >({
        query: ({ entityId, data }) => ({
          method: "POST",
          url: `/api/auth/${entityId}/payments/v1/update_seats`,
          data,
        }),
        invalidatesTags: (result, error, { entityId }) =>
          result
            ? [
                { type: EntitiesApiTag.getSubscriptionInfo, id: entityId },
                { type: EntitiesApiTag.getInvoicesList, id: entityId },
              ]
            : [],
      }),

      updateCardDetails: builder.mutation<
        { secret: string; pub_key: string; type: string },
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          method: "PUT",
          url: `/api/auth/${entityId}/payments/v1/card`,
        }),
      }),

      cancelSubscription: builder.mutation<
        { secret: string; pub_key: string },
        WithApiRequestMeta<{
          entityId: string;
          data: { entity_name: string; reason: string };
        }>
      >({
        query: ({ entityId, data }) => ({
          method: "DELETE",
          url: `/api/auth/${entityId}/payments/v1/subscription`,
          data,
        }),
      }),

      verifyEmail: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          data: { billing_email: string; otp?: string };
        }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/entity/${entityId}/verify_email`,
          method: "POST",
          data,
        }),
        onQueryStarted: (
          { data: { billing_email, otp }, entityId },
          { dispatch, queryFulfilled }
        ) => {
          const patchResult = dispatch(
            entitiesApi.util.updateQueryData(
              "_DEPRECATED_getAllOrgEntitiesList",
              undefined,
              (entities) =>
                entities.map((entity) =>
                  otp && entity.id === entityId
                    ? {
                        ...entity,
                        billingEmail: billing_email,
                        isEmailVerified: true,
                      }
                    : entity
                )
            )
          );

          queryFulfilled.catch(patchResult.undo);
        },
      }),

      getEntityInvites: builder.query<EntityInvitationModel[], void>({
        query: () => ({ url: "/api/auth/invites" }),
        transformResponse: (data: EntityInvitationDto[]) =>
          data.map(transformEntityInvitationDtoToModel),
      }),

      leaveEntity: builder.mutation<
        string,
        WithApiRequestMeta<{ entityId: string; entityRelId: string }>
      >({
        query: ({ entityId, entityRelId }) => ({
          url: `/api/auth/entity/${entityId}/users/${entityRelId}/leave`,
          method: "DELETE",
        }),
        invalidatesTags: () => [EntitiesApiTag.getEntityList],
      }),

      acceptEntityInvitationByLink: builder.mutation<
        { result: "success" },
        WithApiRequestMeta<{
          entityId: string;
          inviteId: string;
        }>
      >({
        query: ({ entityId, inviteId }) => ({
          url: `/api/auth/invite_link/${entityId}/link/${inviteId}`,
          method: "POST",
        }),
        invalidatesTags: [EntitiesApiTag.getEntityList],
      }),

      respondToEntityInvite: builder.mutation<
        void,
        WithApiRequestMeta<RespondToEntityInviteMutationPayload>
      >({
        query: ({ inviteId, action }) => ({
          url: `/api/auth/invites/${inviteId}?action=${action}`,
          method: "PUT",
        }),
        onQueryStarted: ({ inviteId }, { dispatch, queryFulfilled }) => {
          queryFulfilled.then(() => {
            dispatch(
              entitiesApi.util.updateQueryData(
                "getEntityInvites",
                undefined,
                (entities) => entities.filter((item) => item.id !== inviteId)
              )
            );
          });
        },
        invalidatesTags: (result, error, { action }) =>
          !error && action === "accept" ? [EntitiesApiTag.getEntityList] : [],
      }),

      validatePromoCode: builder.mutation<
        PromoCodeInfo,
        WithApiRequestMeta<{ entityId: string; data: { code: string } }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/payments/v1/promo_code`,
          method: "POST",
          data,
        }),
      }),

      retryInvoice: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/payments/v1/retry_invoice`,
          method: "PUT",
        }),
        invalidatesTags: (result, error, { entityId }) =>
          result
            ? [
                EntitiesApiTag.getInvoicesList,
                { type: EntitiesApiTag.getSubscriptionInfo, id: entityId },
              ]
            : [],
      }),

      updateModules: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          data: {
            modules: SubscriptionPlanModuleId[];
          };
        }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/settings/enable/module`,
          method: "POST",
          data,
        }),
        onQueryStarted: ({ data, entityId }, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            entitiesApi.util.updateQueryData(
              "getSubscriptionInfo",
              { entityId },
              (draft) => {
                if (!draft.modules) return;

                draft.modules = draft.modules.map((module) => ({
                  ...module,
                  enabled: data.modules.includes(module.id),
                }));
              }
            )
          );

          queryFulfilled.catch(patchResult.undo);
        },
        invalidatesTags: (result, error, { entityId }) => [
          { type: EntitiesApiTag.getSubscriptionInfo, id: entityId },
        ],
      }),

      _TEST_getCurrentAdvancedDate: builder.query<
        { current_time: string },
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/payments/v1/current_time`,
        }),
      }),

      _TEST_advanceDate: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string; data: { day: number } }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/payments/v1/advance_days`,
          method: "POST",
          data,
        }),
      }),
      _TEST_removePayment: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/payments/v1/remove_payment`,
          method: "POST",
        }),
      }),
      createInvitationLink: builder.mutation<
        InvitationLinkResponse,
        WithApiRequestMeta<{
          entityId: string;
        }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/invite_link/${entityId}`,
          method: "POST",
        }),
      }),

      entityTemplates: builder.query<EntityTemplates, void>({
        query: () => ({
          url: "/api/auth/templates/signup_metadata",
          method: "GET",
        }),
      }),
      getEntitySettings: builder.query<
        {
          task: {
            customer_auth: "disabled" | "enabled";
          };
        },
        WithApiRequestMeta<{
          entityId: string;
        }>
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/settings`,
          method: "GET",
        }),
        providesTags: [EntitiesApiTag.getSettings],
      }),
      saveEntitySettings: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          data: {
            task: {
              customer_auth: "disabled" | "enabled";
            };
          };
        }>
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/settings`,
          method: "POST",
          data,
        }),
        invalidatesTags: [EntitiesApiTag.getSettings],
      }),
    }),
  });

  return entitiesApi;
};

export type EntitiesApi = ReturnType<typeof addEntitiesApi>;
